(ns ui.gallery.card
  "The card of one entry in the grid of the Replicant gallery: the
   preview image with the selection checkbox, the type icon, the name
   and the status message of the story media."
  (:require [ui.gallery.drag :as drag]))

(def type-icons
  {:type.instagram/animation "fa-fast-forward"
   :type.instagram/video "fa-video"
   :type.instagram.story/image "fa-image"})

(defn type-icon
  "Icon that indicates if the story-media is an image, video or
   animation."
  [entry]
  [:div {:style {:position "absolute"
                 :bottom "25px"
                 :right "5px"}}
   [:span.fa-stack.fa-lg
    [:i.fa.fa-circle.fa-stack-2x
     {:style {:color "white"
              :opacity "0.75"}}]
    [:i.fa.fa-stack-1x
     {:class (type-icons (:story-media/type entry))}]]])

(defn status-indicator
  "Allows the server to show a status message on the gallery entry, for
   example while it is processing the entry's video. Not animated on
   purpose, so it does not distract."
  [status-message]
  (when status-message
    [:div.absolute.inset-0.flex.justify-center.items-center.pointer-events-none
     {:style {:z-index 1}}
     [:div {:style {:max-width "80%"
                    :font-size "18px"
                    :color "black"
                    :text-align "center"}}
      status-message]]))

(defn story-media-name
  [name]
  [:div.absolute.bottom-0.left-0.w-full.overflow-hidden.text-ellipsis.text-center.whitespace-nowrap
   {:title name
    :style {:box-sizing "border-box"
            :background-color "rgba(255,255,255,0.5)"
            :padding "5px 2px 0px 2px"
            :font-size "13px"
            :height "24px"
            :color "black"}}
   name])

(defn checkbox
  "The checkbox in the upper left corner of each gallery entry. Not a
   native input: the selection lives in the store, so the browser must
   not toggle anything on its own."
  [gallery entry]
  (let [eid (:story-media/eid entry)
        checked? (contains? (:selected gallery) eid)]
    [:div.absolute.cursor-pointer.p-1
     {:style {:top "5px"
              :left "5px"
              :z-index 1}
      :role "checkbox"
      :aria-checked (str checked?)
      :aria-label "Select"
      :on {:click [[:gallery/checkbox-click eid]]}}
     [:div.w-5.h-5.flex.items-center.justify-center.rounded-sm
      {:class (if checked?
                ["bg-storrito-500"]
                ["bg-gray-200/70" "hover:bg-storrito-300"])}
      (when checked?
        [:i.fas.fa-check.text-white.text-xs])]]))

(defn overlay
  "The click area of the entry. Hovering darkens the entry and shows the
   'Open' hint, a click opens the entry modal (or changes the selection
   with shift / cmd / ctrl held, see `:gallery/card-click`)."
  [entry]
  [:div.absolute.inset-0.flex.items-center.justify-center.cursor-pointer.transition-colors
   {:class ["group-hover:bg-black/40"]
    :on {:click [[:gallery/card-click (:story-media/eid entry)]]}}
   [:span.hidden.group-hover:inline-flex.m-3.text-sm.text-white.justify-center.items-center.px-6.py-2.pointer-events-none
    {:style {:background-color "#363636"}}
    "Open"]])

(defn card
  "The card of the gallery `entry`. Dragging it moves the entry (or the
   whole selection, when the entry is selected) into a folder."
  [gallery entry]
  (let [eid (:story-media/eid entry)
        drag (:drag gallery)]
    [:div.group.relative.bg-white.rounded-md.text-gray-700.shadow-lg.transition-all.duration-200
     {;; keyed by the entry: when a moved entry leaves the grid with the
      ;; next refresh, the entry that takes its slot gets a fresh element
      ;; instead of inheriting the faded-out one and zooming back in,
      ;; which looked like the moved entry was still there.
      :replicant/key eid
      :class (concat (when (drag/dragged-media-eid? drag eid)
                       ["opacity-40"])
                     (when (drag/moving-out? gallery eid)
                       ["opacity-0" "scale-75" "pointer-events-none"]))
      :style {:width "100%"
              :height "100%"
              :background-position "center"
              :background-size "cover"
              :background-repeat "no-repeat"
              :background-color "grey"
              :background-image (when-let [url (:story-media/preview-url entry)]
                                  (str "url(" url ")"))}
      :draggable true
      :on {:dragstart [[:gallery/drag-start-media eid]]
           :dragend [[:gallery/drag-end]]}}
     (overlay entry)
     (status-indicator (:story-media/status-message entry))
     (checkbox gallery entry)
     (type-icon entry)
     (when-let [name (:story-media/name entry)]
       (story-media-name name))]))
